import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Heart, Trash2 } from 'lucide-react';
import Collection from '@/components/Collection';
import Navbar from '@/components/Navbar';
import {
  Collection as CollectionType,
  Category,
  VideoItem,
  createCollection,
  getCollections,
  getCollectionWithVideos,
  addVideoToCollection,
  removeVideoFromCollection,
  updateCollection,
  removeCollection,
} from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Card, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import ImportPlaylist from '@/components/ImportPlaylist';

const Index: React.FC = () => {
  const { user, loading } = useAuth();
  const [collections, setCollections] = useState<CollectionType[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<CollectionType & { videos?: VideoItem[]; categories?: Category[] } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const { toast } = useToast();

  if (!loading && !user) {
    return <Navigate to="/auth" />;
  }

  useEffect(() => {
    if (user) loadCollections();
  }, [user]);

  const loadCollections = async () => {
    try {
      const data = await getCollections();
      setCollections(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading collections:', error);
      toast({
        title: "Error",
        description: "Failed to load collections",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!confirm("Are you sure you want to delete this collection?")) return;
    try {
      await removeCollection(collectionId);
      setCollections(prev => prev.filter(c => c.id !== collectionId));
      if (selectedCollection?.id === collectionId) {
        setSelectedCollection(null);
      }
      toast({ title: "Deleted", description: "Collection deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete collection", variant: "destructive" });
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      toast({ title: "Error", description: "Enter a name", variant: "destructive" });
      return;
    }

    try {
      const newCollection = await createCollection({
        name: newCollectionName.trim(),
        description: newCollectionDescription.trim(),
        user_id: user!.id,
        is_public: false,
        tags: [],
        categories: []
      });

      setCollections([newCollection, ...collections]);
      setSelectedCollection(newCollection);
      setIsCreateDialogOpen(false);
      setNewCollectionName('');
      setNewCollectionDescription('');
      toast({ title: "Success", description: "Collection created" });
    } catch (error) {
      console.error(error);
      toast({ title: "Error", description: "Failed to create collection", variant: "destructive" });
    }
  };

  const handleSelectCollection = async (collection: CollectionType) => {
    try {
      const full = await getCollectionWithVideos(collection.id!);
      setSelectedCollection(full);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load collection", variant: "destructive" });
    }
  };

  const handleAddVideo = async (video: VideoItem, collectionId: string) => {
    try {
      await addVideoToCollection(collectionId, video);
      if (selectedCollection?.id === collectionId) {
        const updated = await getCollectionWithVideos(collectionId);
        setSelectedCollection(updated);
      }
      toast({ title: "Success", description: "Video added" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleRemoveVideo = async (videoId: string) => {
    if (!selectedCollection?.id) return;
    try {
      await removeVideoFromCollection(selectedCollection.id, videoId);
      const updated = await getCollectionWithVideos(selectedCollection.id);
      setSelectedCollection(updated);
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove video", variant: "destructive" });
    }
  };

  const handlePublicChange = async (isPublic: boolean) => {
    if (!selectedCollection?.id) return;

    try {
      const updated = await updateCollection(selectedCollection.id, {
        ...selectedCollection,
        is_public: isPublic,
      });
      await refreshCollection(updated.id!);
      toast({
        title: isPublic ? "Now public" : "Now private",
        description: isPublic
          ? "Anyone with the link can view"
          : "Only you can view"
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update visibility", variant: "destructive" });
    }
  };

  const handleTagsChange = async (tags: string[]) => {
    if (!selectedCollection?.id) return;

    try {
      await updateCollection(selectedCollection.id, {
        ...selectedCollection,
        tags,
      });
      await refreshCollection(selectedCollection.id);
      toast({ title: "Tags updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update tags", variant: "destructive" });
    }
  };

  const handleCategoryChange = async (categories: Category[]) => {
    if (!selectedCollection?.id) return;

    try {
      await updateCollection(selectedCollection.id, {
        ...selectedCollection,
        categories,
      });
      await refreshCollection(selectedCollection.id);
      toast({ title: "Categories updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update categories", variant: "destructive" });
    }
  };

  const handleSaveCollection = async () => {
    if (!selectedCollection?.id) return;
    try {
      const updated = await updateCollection(selectedCollection.id, selectedCollection);
      await refreshCollection(updated.id!);
      toast({ title: "Saved successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save collection", variant: "destructive" });
    }
  };

  const refreshCollection = async (id: string) => {
    const updated = await getCollectionWithVideos(id);
    setCollections(c => c.map(col => col.id === id ? updated : col));
    setSelectedCollection(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-center">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8 max-w-full lg:max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-4">
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <Button className="w-full" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus size={16} className="mr-2" />
                New Collection
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Collection</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Name
                    </label>
                    <Input
                      id="name"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="description" className="text-sm font-medium">
                      Description
                    </label>
                    <Textarea
                      id="description"
                      value={newCollectionDescription}
                      onChange={(e) => setNewCollectionDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreateCollection}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="w-full">
              <ImportPlaylist />
            </div>

            <div className="space-y-2">
              {collections.map(collection => (
                <Card
                  key={collection.id}
                  className={`cursor-pointer ${selectedCollection?.id === collection.id ? 'bg-accent' : ''}`}
                  onClick={() => handleSelectCollection(collection)}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium truncate">{collection.name}</h3>
                      <div className="flex items-center gap-2">
                        {collection.is_subscribed && (
                          <Heart size={16} className="text-accent fill-current" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCollection(collection.id);
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    {collection.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {collection.tags.map(tag => (
                          <span
                            key={tag}
                            className="text-xs bg-accent/10 text-accent-foreground px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            {selectedCollection ? (
              <Collection
                collection={selectedCollection}
                onNameChange={(name) => setSelectedCollection({ ...selectedCollection, name })}
                onDescriptionChange={(description) => setSelectedCollection({ ...selectedCollection, description })}
                onTagsChange={handleTagsChange}
                onCategoryChange={handleCategoryChange}
                onPublicChange={handlePublicChange}
                onRemoveVideo={handleRemoveVideo}
                onSave={handleSaveCollection}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Select a collection or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Index;